/**
 * What a fee schedule save would change, in figures a partner can check
 * before it goes live: each fee priced by the shared engine at a few sample
 * prices, before and after, plus plain-English notes for settings the sample
 * figures may not show (GST, LVR, loading, stages added or removed).
 */
import { PAYABLE_STAGES, PROPERTY_TYPE_LABELS, REFERRAL_STAGE_LABELS, quoteReferralFee } from './schemas';
import { formatBps } from './money';
/** $750,000, $1.5m, $3m: low, middle and high of a typical buyer's agent book. */
export const SAMPLE_PRICES_CENTS = [75000000, 150000000, 300000000];
function quote(schedule, priceCents, propertyType) {
    if (!schedule)
        return null;
    try {
        return quoteReferralFee(schedule, { purchasePriceCents: priceCents, loanAmountCents: null, propertyType });
    }
    catch {
        return null;
    }
}
const sameList = (a, b) => a.length === b.length && [...a].sort().join() === [...b].sort().join();
export function summariseFeeChange(before, after, propertyType = 'investment', prices = SAMPLE_PRICES_CENTS) {
    const quotes = prices.map((p) => ({ p, b: quote(before, p, propertyType), a: quote(after, p, propertyType) }));
    const rows = [];
    const push = (key, label, pick) => {
        const cells = quotes.map(({ p, b, a }) => ({ priceCents: p, beforeIncGstCents: pick(b), afterIncGstCents: pick(a) }));
        if (cells.every((c) => c.beforeIncGstCents === null && c.afterIncGstCents === null))
            return;
        rows.push({ key, label, cells, changed: cells.some((c) => c.beforeIncGstCents !== c.afterIncGstCents) });
    };
    for (const stage of PAYABLE_STAGES) {
        push(stage, `Broker, ${REFERRAL_STAGE_LABELS[stage].toLowerCase()}`, (q) => q?.payouts[stage]?.incGstCents ?? null);
    }
    push('agent_fee', 'Your fee', (q) => q?.agentFee?.incGstCents ?? null);
    const notes = [];
    if (before) {
        for (const stage of PAYABLE_STAGES) {
            const label = REFERRAL_STAGE_LABELS[stage].toLowerCase();
            if (!before.payouts[stage] && after.payouts[stage])
                notes.push(`Brokers are now paid at ${label}.`);
            if (before.payouts[stage] && !after.payouts[stage])
                notes.push(`Brokers are no longer paid at ${label}.`);
        }
        if (before.gstRateBps !== after.gstRateBps)
            notes.push(`GST changes from ${formatBps(before.gstRateBps)} to ${formatBps(after.gstRateBps)}.`);
        if (before.defaultLvrBps !== after.defaultLvrBps) {
            notes.push(`Default LVR changes from ${formatBps(before.defaultLvrBps)} to ${formatBps(after.defaultLvrBps)}.`);
        }
        const bl = before.loading;
        const al = after.loading;
        if (bl.rateBps !== al.rateBps ||
            !sameList(bl.propertyTypes, al.propertyTypes) ||
            bl.appliesToAgentFee !== al.appliesToAgentFee ||
            bl.appliesToPayouts !== al.appliesToPayouts) {
            notes.push(`Loading by property type changes (now ${formatBps(al.rateBps)}). The figures are for ${PROPERTY_TYPE_LABELS[propertyType].toLowerCase()} purchases.`);
        }
    }
    return { rows, notes, anyChange: !before || notes.length > 0 || rows.some((r) => r.changed) };
}
